import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import ApiService from '../../services/ApiService';
import { useMessage } from '../common/MessageDisplay';

const FindFlightsPage = () => {
  const { ErrorDisplay, SuccessDisplay, showError } = useMessage();

  const [flights, setFlights] = useState([]);
  const [airports, setAirports] = useState([]);
  const [loading, setLoading] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useState({
    departureIataCode: '',
    arrivalIataCode: '',
    departureDate: '',
  });

  useEffect(() => {
    const fetchAirports = async () => {
      try {
        const response = await ApiService.getAllAirports();
        setAirports(response?.data || []);
      } catch (error) {
        showError('Failed to load airports. Please try again later.');
      }
    };

    fetchAirports();
  }, [showError]);

  const fetchFlights = async (params) => {
    try {
      setLoading(true);

      const response = await ApiService.searchFlights(
        params.departureIataCode,
        params.arrivalIataCode,
        params.departureDate
      );

      setFlights(response?.data || []);
    } catch (error) {
      showError(
        error?.response?.data?.message || 'Failed to load flights. Please try again later.'
      );
      setFlights([]);
    } finally {
      setLoading(false);
    }
  };

  const didFetchRef = useRef(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    const initialParams = {
      departureIataCode: params.get('departureIataCode') || '',
      arrivalIataCode: params.get('arrivalIataCode') || '',
      departureDate: params.get('departureDate') || '',
    };

    // ✅ actualiza solo si cambió
    setSearchParams((prev) => {
      const same =
        prev.departureIataCode === initialParams.departureIataCode &&
        prev.arrivalIataCode === initialParams.arrivalIataCode &&
        prev.departureDate === initialParams.departureDate;

      return same ? prev : initialParams;
    });

    const hasAll =
      initialParams.departureIataCode &&
      initialParams.arrivalIataCode &&
      initialParams.departureDate;

    if (!hasAll) {
      setFlights([]);
      setLoading(false);
      didFetchRef.current = false;
      return;
    }

    // ✅ evita doble fetch en StrictMode
    if (didFetchRef.current) return;
    didFetchRef.current = true;

    fetchFlights(initialParams);
  }, [location.search]);

  const handleSearch = (e) => {
    e.preventDefault();

    if (
      !searchParams.departureIataCode ||
      !searchParams.arrivalIataCode ||
      !searchParams.departureDate
    ) {
      showError('Please fill in all search fields.');
      return;
    }

    const query = new URLSearchParams();
    query.set('departureIataCode', searchParams.departureIataCode);
    query.set('arrivalIataCode', searchParams.arrivalIataCode);
    query.set('departureDate', searchParams.departureDate);

    navigate(`/flights?${query.toString()}`);
  };

  const handleSwapAirports = () => {
    setSearchParams((prev) => ({
      ...prev,
      departureIataCode: prev.arrivalIataCode,
      arrivalIataCode: prev.departureIataCode,
    }));
  };

  const formatAirportOption = (airport) => {
    return `${airport.iataCode} (${airport.city}), ${airport.name}`;
  };

  const formatTime = (dateTime) => {
    return new Date(dateTime).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const formatDate = (dateTime) => {
    return new Date(dateTime).toLocaleDateString([], {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
    });
  };

  const formatMoney = (value) => {
    const n = Number(value ?? 0);
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP', // cámbialo a 'USD' si tu precio está en dólares
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(n);
  };

  const calculateDuration = (departureTime, arrivalTime) => {
    const dep = new Date(departureTime);
    const arr = new Date(arrivalTime);
    const diff = arr - dep;

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    return `${hours}h ${minutes}m`;
  };

  return (
    <div className="find-flights-page">
      <div className="container">
        <ErrorDisplay />
        <SuccessDisplay />

        <div className="search-section">
          <h2>Find Your Flights</h2>

          <form onSubmit={handleSearch} className="search-form">
            <div className="search-row">
              <div className="form-group">
                <label>From</label>
                <select
                  value={searchParams.departureIataCode}
                  onChange={(e) =>
                    setSearchParams((prev) => ({
                      ...prev,
                      departureIataCode: e.target.value,
                    }))
                  }
                  required
                >
                  <option value="">Select Departure Airport</option>
                  {airports.map((airport) => (
                    <option key={`dep-${airport.iataCode}`} value={airport.iataCode}>
                      {formatAirportOption(airport)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>To</label>
                <select
                  value={searchParams.arrivalIataCode}
                  onChange={(e) =>
                    setSearchParams((prev) => ({
                      ...prev,
                      arrivalIataCode: e.target.value,
                    }))
                  }
                  required
                >
                  <option value="">Select Arrival Airport</option>
                  {airports
                    .filter((airport) => airport.iataCode !== searchParams.departureIataCode)
                    .map((airport) => (
                      <option key={`arr-${airport.iataCode}`} value={airport.iataCode}>
                        {formatAirportOption(airport)}
                      </option>
                    ))}
                </select>
              </div>

              <div className="form-group">
                <label>Departure Date</label>
                <input
                  type="date"
                  value={searchParams.departureDate}
                  onChange={(e) =>
                    setSearchParams((prev) => ({
                      ...prev,
                      departureDate: e.target.value,
                    }))
                  }
                  min={new Date().toISOString().split('T')[0]}
                  required
                />
              </div>

              <button type="button" className="ff-button" onClick={handleSwapAirports}>
                Swap
              </button>

              <button type="submit" className="ff-button">
                Search Flights
              </button>
            </div>
          </form>
        </div>

        <div className="results-section">
          {loading ? (
            <div className="loading">Loading flights...</div>
          ) : flights.length > 0 ? (
            <div className="flights-list">
              {flights.map((flight) => (
                <div key={flight.id} className="flight-card">
                  <div className="flight-header">
                    <div className="flight-number">{flight.flightNumber}</div>
                    <div className={`flight-status ${flight.status?.toLowerCase?.() || ''}`}>
                      {flight.status}
                    </div>
                  </div>

                  <div className="flight-details">
                    <div className="departure">
                      <div className="time">{formatTime(flight.departureTime)}</div>
                      <div className="date">{formatDate(flight.departureTime)}</div>
                      <div className="airport">
                        {flight.departureAirport?.iataCode} - {flight.departureAirport?.name}
                      </div>
                    </div>

                    <div className="duration">
                      <div className="line"></div>
                      <div className="duration-text">
                        {calculateDuration(flight.departureTime, flight.arrivalTime)}
                      </div>
                      <div className="line"></div>
                    </div>

                    <div className="arrival">
                      <div className="time">{formatTime(flight.arrivalTime)}</div>
                      <div className="date">{formatDate(flight.arrivalTime)}</div>
                      <div className="airport">
                        {flight.arrivalAirport?.iataCode} - {flight.arrivalAirport?.name}
                      </div>
                    </div>

                    <div className="price">{formatMoney(flight.basePrice)}</div>
                  </div>

                  <div className="flight-actions">
                    <Link
                      to={`/book-flight/${flight.id}`}
                      state={{ flight }}
                      className="book-button"
                    >
                      Book Now
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-flights">
              {searchParams.departureIataCode || searchParams.arrivalIataCode ? (
                <p>No flights found for the selected criteria.</p>
              ) : (
                <p>Please select departure and arrival airports to search for flights.</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FindFlightsPage;
